// Background territory tracker — keeps pinging /api/wyngx/territory/ping
// while the iOS WebView is suspended or the screen is locked, using the
// `@capacitor-community/background-geolocation` native plugin. Without
// this, Wyngx Territory only fills hexes while the app is in foreground
// with the screen on; with this, your hexes grow as you walk normally.
//
// Architecture:
//   • One global watcher is added when the user is authenticated on
//     iOS / Android. The watcher fires every time the OS produces a
//     new GPS fix (or every `distanceFilter` meters of movement).
//   • Each fix posts to `/api/wyngx/territory/ping`. The backend's
//     bridge + flood-fill logic does the rest.
//   • Client-side throttle: drop any fix less than `MIN_INTERVAL_MS`
//     after the previous successful post, so the server's rate limit
//     never trips while still capturing dense walking traces.
//
// Permissions:
//   • Info.plist already declares NSLocationAlways(AndWhenInUse) since
//     the Territory rebuild. The plugin uses these strings verbatim.
//   • If the user denies, we silently no-op — the foreground tracker
//     in CrewMap still works.

import { useEffect, useRef } from "react";
import { Capacitor } from "@capacitor/core";
import api from "@/api";

const MIN_INTERVAL_MS = 8_000;      // smoother polyline: ~1 ping per 8s
const MIN_DISTANCE_M  = 5;          // OS-level filter — denser walk-trail


export default function useBackgroundTerritory(enabled) {
    const watcherIdRef  = useRef(null);
    const lastPostRef   = useRef({ ts: 0 });

    useEffect(() => {
        if (!enabled) return;
        // Web build doesn't have the plugin runtime — no-op.
        if (!Capacitor.isNativePlatform()) return;

        let cancelled = false;
        let pluginRef = null;

        (async () => {
            try {
                // Dynamic import so the web build doesn't crash if the
                // plugin's native runtime is missing.
                const mod = await import(
                    "@capacitor-community/background-geolocation"
                );
                if (cancelled) return;
                const BackgroundGeolocation = mod.BackgroundGeolocation;
                pluginRef = BackgroundGeolocation;

                const id = await BackgroundGeolocation.addWatcher(
                    {
                        // iOS shows these strings in the notification
                        // shade while the background blue bar is active.
                        backgroundMessage:
                            "Wyngx Territory loopt mee — hexes worden geclaimd terwijl jij wandelt.",
                        backgroundTitle:  "WYNG",
                        // Always request — if user picked "While Using"
                        // before, this is a no-op. If "Never", the
                        // callback receives a NOT_AUTHORIZED error.
                        requestPermissions: true,
                        stale: false,
                        distanceFilter: MIN_DISTANCE_M,
                    },
                    (location, error) => {
                        if (error) {
                            // We don't surface a toast here; the user
                            // sees the iOS prompt itself. Skipping a
                            // post is benign.
                            return;
                        }
                        if (!location) return;
                        const now = Date.now();
                        if (now - lastPostRef.current.ts < MIN_INTERVAL_MS) return;
                        lastPostRef.current.ts = now;
                        api.post("/wyngx/routes/ping", {
                            lat:       location.latitude,
                            lng:       location.longitude,
                            speed_mps: location.speed ?? undefined,
                        }).catch(() => { /* offline / transient — silent */ });
                    },
                );
                watcherIdRef.current = id;
            } catch (e) {
                console.warn("[wyngx] background tracker failed to start", e);
            }
        })();

        return () => {
            cancelled = true;
            const id = watcherIdRef.current;
            if (id && pluginRef) {
                try { pluginRef.removeWatcher({ id }); } catch { /* */ }
            }
            watcherIdRef.current = null;
        };
    }, [enabled]);
}
